﻿
/**
 * Vendor Integration Layer
 * Rule: SDK -> Store bridge with UI feedback
 */
import { toast } from 'sonner';
import i18next from 'i18next';
import { fetchDropdownOptionsFromReference } from '@/lib/reference-dropdown.helper.js';
import { useVendorsStore } from '@/store/vendors.store';
import { useTenantStore } from '@/store/tenant.store';
import { Logger } from '@/lib/app-log.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class VendorIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    /**
     * Fetch vendors and update store
     */
    async fetchVendors(page = 1, pageSize = 10, search = '', sortBy = 'createdAt', sortOrder = 'desc', columnFilters = {}) {
        const store = useVendorsStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);
        store.clearError();

        if (!tenantId) {
            store.setLoading(false);
            const message = getIntegrationErrorMessage({ message: 'Tenant context required' });
            return { success: false, error: message };
        }

        try {
            let response;
            let effectivePage = 1;
            let effectivePageSize = 10;

            if (typeof page === 'object' && page !== null) {
                // Object-style params (used by DependencySelectorDialog)
                const params = page || {};
                effectivePage = params.page ?? 1;
                effectivePageSize = params.pageSize ?? params.limit ?? 10;

                const {
                    search: objSearch = '',
                    sortBy: objSortBy = 'createdAt',
                    sortOrder: objSortOrder = 'desc'
                } = params;

                // Pass through any pre-built filter_ keys and excludeIds
                const extra = Object.entries(params).reduce((acc, [key, value]) => {
                    if (key.startsWith('filter_') || key === 'excludeIds') {
                        acc[key] = value;
                    }
                    return acc;
                }, {});

                response = await this.service.getAll({
                    page: effectivePage,
                    pageSize: effectivePageSize,
                    limit: effectivePageSize,
                    search: objSearch,
                    sortBy: objSortBy,
                    sortOrder: objSortOrder,
                    tenantId,
                    ...extra
                });
            } else {
                // Legacy positional arguments
                effectivePage = page;
                effectivePageSize = pageSize;

                response = await this.service.getAll({
                    page,
                    pageSize,
                    search,
                    sortBy,
                    sortOrder,
                    tenantId,
                    ...Object.entries(columnFilters).reduce((acc, [key, value]) => {
                        if (value && value.length > 0) {
                            acc[`filter_${key}`] = value;
                        }
                        return acc;
                    }, {})
                });
            }

            const { data, pagination, uniqueValues } = response;
            store.setVendors(data || []);

            if (pagination) {
                store.setPaginationData({
                    currentPage: pagination.page || effectivePage,
                    totalPages: pagination.totalPages || 1,
                    totalItems: pagination.total || 0,
                    itemsPerPage: pagination.pageSize || effectivePageSize,
                    hasNextPage: pagination.page < pagination.totalPages,
                    hasPreviousPage: pagination.page > 1,
                });
            }

            if (uniqueValues) {
                store.setUniqueValues(uniqueValues);
            }

            return {
                success: true,
                data: data || [],
                pagination: pagination || {},
                uniqueValues: uniqueValues || {}
            };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            store.setError(errorMessage);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Create vendor
     */
    async createVendor(data) {
        const store = useVendorsStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            const message = getIntegrationErrorMessage({ message: 'Tenant context required' });
            return { success: false, error: message };
        }

        try {
            const payload = { ...data, tenantId };
            const response = await this.service.create(payload);

            toast.success(i18next.t('vendors:messages.createSuccess', { defaultValue: 'Vendor created successfully' }));

            if (store.closeForm) store.closeForm();
            store.setSuccess(true);
            await this.fetchVendors(); // Refresh list

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            store.setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Vendor list for asset detail / selectors without mutating the global vendor store.
     */
    async fetchVendorsDetached(params = {}) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            return { success: false, data: [], pagination: {}, uniqueValues: {}, meta: { total: 0 } };
        }
        try {
            const response = await this.service.getAll({
                page: params.page ?? 1,
                pageSize: params.pageSize ?? params.limit ?? 10,
                limit: params.limit ?? params.pageSize ?? 10,
                search: params.search ?? '',
                sortBy: params.sortBy ?? 'createdAt',
                sortOrder: params.sortOrder ?? params.sortDirection ?? 'desc',
                tenantId,
                ...(params.linkedAssetId ? { linkedAssetId: params.linkedAssetId } : {}),
                ...(params.excludeIds != null && params.excludeIds !== ''
                    ? { excludeIds: params.excludeIds }
                    : {}),
                ...Object.entries(params).reduce((acc, [key, value]) => {
                    if (key.startsWith('filter_')) acc[key] = value;
                    return acc;
                }, {}),
            });
            const pagination = response.pagination || {};
            return {
                success: true,
                data: response.data || [],
                meta: {
                    total: pagination.total ?? 0,
                    page: pagination.page ?? 1,
                    pages: pagination.totalPages ?? 0,
                },
                uniqueValues: response.uniqueValues || {},
            };
        } catch (error) {
            return {
                success: false,
                data: [],
                meta: { total: 0 },
                uniqueValues: {},
                error: this._getErrorMessage(error),
            };
        }
    }

    /**
     * Update vendor
     * @param {{ skipStoreLoading?: boolean, skipSuccessToast?: boolean, skipListRefresh?: boolean }} [opts]
     */
    async updateVendor(id, data, opts = {}) {
        const store = useVendorsStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        if (!opts.skipStoreLoading) store.setLoading(true);

        if (!tenantId) {
            if (!opts.skipStoreLoading) store.setLoading(false);
            const message = getIntegrationErrorMessage({ message: 'Tenant context required' });
            return { success: false, error: message };
        }

        try {
            const response = await this.service.update(id, data);

            if (!opts.skipSuccessToast) {
                toast.success(i18next.t('vendors:messages.updateSuccess', { defaultValue: 'Vendor updated successfully' }));
            }

            if (store.closeForm) store.closeForm();
            store.setSuccess(true);
            if (!opts.skipListRefresh) {
                await this.fetchVendors();
            }

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            store.setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            if (!opts.skipStoreLoading) store.setLoading(false);
        }
    }

    /**
     * Delete vendor
     */
    async deleteVendor(id) {
        const store = useVendorsStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            const message = getIntegrationErrorMessage({ message: 'Tenant context required' });
            return { success: false, error: message };
        }

        try {
            await this.service.delete(id);

            toast.success(i18next.t('vendors:messages.deleteSuccess', { defaultValue: 'Vendor deleted successfully' }));

            if (store.closeDeleteDialog) store.closeDeleteDialog();
            store.setSuccess(true);
            await this.fetchVendors(); // Refresh list

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Bulk delete vendors
     */
    async bulkDeleteVendors(ids) {
        const store = useVendorsStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            const message = getIntegrationErrorMessage({ message: 'Tenant context required' });
            return { success: false, error: message };
        }

        try {
            await this.service.bulkDelete(ids);

            toast.success(i18next.t('vendors:messages.bulkDeleteSuccess', {
                count: ids.length,
                defaultValue: `${ids.length} vendors deleted successfully`,
            }));
            store.setSuccess(true);
            await this.fetchVendors(); // Refresh list

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    async fetchVendorsForDropdown() {
        const store = useVendorsStore.getState();
        try {
            const result = await fetchDropdownOptionsFromReference('vendor', async () => {
                const response = await this.service.getDropdownParams();
                return { success: true, data: response.data || [] };
            });
            store.setVendorsForDropdown(result.data || []);
        } catch (error) {
            Logger.error('Failed to fetch vendor dropdown', { error: error?.message });
        }
    }

    async fetchVendorById(id) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            const message = getIntegrationErrorMessage({ message: 'Tenant context required' });
            return { success: false, error: message };
        }
        try {
            const response = await this.service.getById(id);
            const data = response?.data ?? response;
            return { success: true, data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            return { success: false, error: errorMessage };
        }
    }

    async fetchVendorSummary() {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            const message = getIntegrationErrorMessage({ message: 'Tenant context required' });
            return { success: false, error: message };
        }
        try {
            return await this.service.getSummary();
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            return { success: false, error: errorMessage };
        }
    }

    /**
     * Map error to user-friendly message
     */
    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error, {
            notFoundDefault: 'Vendor not found',
        });
    }
}
